import { createFilter } from 'react-select';
import { useMemo, useState, useEffect, ComponentType } from 'react';

import { Select } from 'components/select';
import { TimeDropdownIndicator } from 'components/select/components';
import { parseTimeInput, parseRangeInput } from 'utils/show-hours-parser';

import { getOptions } from './helpers';
import { TimePickerProps } from './types';

const defaultFilter = createFilter({ ignoreAccents: false });

const nearestOption = (options: { value: string }[], value: number) => {
  let best = options[0];
  options.forEach((option) => {
    if (
      Math.abs(Number(option.value) - value) <
      Math.abs(Number(best.value) - value)
    ) {
      best = option;
    }
  });
  return best;
};

export const TimePicker = ({
  after,
  value,
  typeable,
  onChange,
  minValue,
  timeRole,
  step = 15,
  components,
  onRangeInput,
  isSearchable = true,
  setDefaultSelectedValue,
  ...props
}: TimePickerProps) => {
  const options = useMemo(() => getOptions(step, minValue), [step, minValue]);
  const [inputValue, setInputValue] = useState('');

  const selectedValue = useMemo(() => {
    if (!value && value !== 0) {
      return undefined;
    }
    const sorted = [...options];
    sorted.sort(({ value: a }, { value: b }) => {
      const aDiff = Math.abs(Number(a) - value);
      const bDiff = Math.abs(Number(b) - value);
      if (aDiff === bDiff) {
        return 0;
      }
      return aDiff > bDiff ? 1 : -1;
    });
    return sorted[0];
  }, [options, value]);

  useEffect(() => {
    const checkedValue = Number(selectedValue?.value);

    if (
      selectedValue &&
      setDefaultSelectedValue &&
      typeof value === 'number' &&
      checkedValue !== value
    ) {
      onChange(checkedValue);
    }
  }, [selectedValue?.value]);

  const typed = useMemo(() => {
    if (!typeable || !inputValue.trim() || !options.length) {
      return undefined;
    }
    const ms = parseTimeInput(inputValue, timeRole, after);
    return ms === undefined ? undefined : nearestOption(options, ms);
  }, [typeable, inputValue, timeRole, after, options]);

  const typedRange =
    typeable && onRangeInput && !typed
      ? parseRangeInput(inputValue)
      : undefined;

  const typingProps = typeable
    ? {
        inputValue,
        noOptionsMessage: () =>
          typedRange ? 'Press Enter to fill From and To' : 'Not a time',
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === 'Enter' && typedRange) {
            event.preventDefault();
            onRangeInput?.(typedRange);
            setInputValue('');
          }
        },
        filterOption: (
          option: { label: string; value: string; data: unknown },
          text: string
        ) =>
          typed
            ? option.value === typed.value
            : !typedRange && defaultFilter(option as any, text),
        onInputChange: (text: string, meta: { action: string }) => {
          if (meta.action === 'input-change') {
            setInputValue(text);
            return;
          }
          if (meta.action === 'input-blur') {
            if (typed && Number(typed.value) !== value) {
              onChange(Number(typed.value));
            } else if (typedRange) {
              onRangeInput?.(typedRange);
            }
          }
          setInputValue('');
        },
      }
    : {};

  return (
    <Select
      defaultControl
      options={options}
      isSearchable={isSearchable}
      // Always controlled: null (not undefined) when there is no time, so
      // react-select never falls back to the option it last picked itself
      // (Dev 23 Oct: a Closed row kept showing a hand-picked 9:00 am).
      value={selectedValue ?? null}
      components={{
        DropdownIndicator: TimeDropdownIndicator as ComponentType<any>,
        ...components,
      }}
      onChange={(newValue) => {
        // @ts-ignore
        const result = newValue?.value;
        onChange(result ? Number(result) : undefined);
      }}
      {...typingProps}
      {...props}
    />
  );
};

export type { TimePickerProps };
